/**
 * The bottom-right toast layer (``chrome-spec.md`` §"Toast layer").
 *
 * Anchored 24 px from the bottom-right edges, newest at the top, at most
 * :data:`notification-timing.TOAST_STACK_CAP` visible — the store enforces
 * the cap and the eviction, so this component only renders what it is
 * given.
 *
 * # Why the wrapper is ``pointer-events-none``
 *
 * The layer spans a corner of the viewport but must not swallow clicks
 * aimed at the map or a panel beneath it. The container disables pointer
 * events and each :class:`Toast` re-enables them for itself — the standard
 * overlay pattern, and the reason a toast stack does not create a dead
 * zone over the operator's canvas.
 *
 * # Landmark
 *
 * The region is labelled ``Notifications`` so keyboard and screen-reader
 * users can reach it as a landmark (REQ-UI-059 — landmark structure). The
 * individual live-region roles live on each toast, not here: one live
 * region wrapping a changing list re-announces the whole list on every
 * change.
 *
 * Cross-references:
 *   - ``chrome-spec.md`` §"Toast layer" (geometry) / §"Toast layer (production)".
 *   - storyboard Flow F Frame 3 (stacking) + §Behavior notes.
 *   - REQ-UI-059 (landmarks + keyboard).
 */
import { useNotificationStore } from "@/notifications/notification-store";
import { Toast } from "@/notifications/toast";

/**
 * Render the live toast stack. Mounted once, near the end of the app shell
 * so it paints above the canvas. Renders nothing (no empty landmark) when
 * there are no toasts.
 */
export function ToastHost(): React.ReactElement | null {
  const toasts = useNotificationStore((s) => s.toasts);
  const close = useNotificationStore((s) => s.close);
  const setPaused = useNotificationStore((s) => s.setPaused);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Notifications"
      data-testid="toast-host"
      className="pointer-events-none fixed bottom-6 right-6 z-50 flex flex-col gap-2"
    >
      {toasts.map((notification) => (
        // Keyed on createdAt as well as id: a dedupe replacement remounts
        // the toast so its countdown restarts from scratch (see Toast).
        <Toast
          key={`${notification.id}:${notification.createdAt}`}
          notification={notification}
          onClose={close}
          onPauseChange={setPaused}
        />
      ))}
    </section>
  );
}
